import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import {Award, Download, Loader2, AlertCircle} from 'lucide-react'
import { baixarCertificadoPdf } from '@/utils/certificadoPdf'

function Certificado() {
  const [registro, setRegistro] = useState<any>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState('')
  const [tokenCriarSenha, setTokenCriarSenha] = useState('')
  const [wppContato, setWppContato] = useState('5581988671210')
  const [nomeConsultora, setNomeConsultora] = useState('')
  const [baixando, setBaixando] = useState(false)

  useEffect(() => {
    void carregar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function carregar() {
    setCarregando(true)
    setErro('')
    try {
      let whatsapp = ''
      try {
        whatsapp = sessionStorage.getItem('capacitacao_whatsapp') || ''
      } catch {
        // ignore
      }
      if (!whatsapp) {
        setErro('Não encontramos sua inscrição.')
        setCarregando(false)
        return
      }
      const resp = await lumi.entities.capacitacao.list({
        filter: { whatsapp },
        limit: 1,
      })
      const item = resp?.list?.[0]
      if (!item) {
        setErro('Inscrição não encontrada.')
        setCarregando(false)
        return
      }
      setRegistro(item)

      // Buscar consultora que indicou (para bloco "Dúvidas?")
      const indicadoPor = item.indicadoPor ?? ''
      if (indicadoPor && indicadoPor.toLowerCase() !== 'monica') {
        try {
          const respC = await lumi.entities.consultoras.list({
            filter: { usuario: indicadoPor },
            limit: 1,
          })
          const consultora = respC?.list?.[0]
          if (consultora?.whatsapp) {
            const numLimpo = consultora.whatsapp.replace(/\D/g, '')
            if (numLimpo) {
              setWppContato(numLimpo)
            }
          }
          if (consultora?.nome) {
            setNomeConsultora(consultora.nome)
          }
        } catch {
          // fallback para Mônica
        }
      }

      // Usar tokenCriarSenha (campo exclusivo, 7 dias) para o link de criação de senha.
      // Se ainda não existe (registro antigo), gerar agora e salvar.
      if (!item.tokenCriarSenha) {
        const exp7d = Date.now() + 1000 * 60 * 60 * 24 * 7
        const novoToken = btoa(JSON.stringify({ email: item.email ?? '', exp: exp7d }))
        const expira = new Date(exp7d).toISOString()
        await lumi.entities.capacitacao.update(item._id, {
          tokenCriarSenha: novoToken,
          tokenCriarSenhaExpiracao: expira,
          updatedAt: new Date().toISOString(),
        })
        setTokenCriarSenha(novoToken)
      } else {
        setTokenCriarSenha(item.tokenCriarSenha)
      }
    } catch (e) {
      console.error('[certificado] erro ao carregar', e)
      setErro('Não foi possível carregar seu certificado. Tente novamente.')
    } finally {
      setCarregando(false)
    }
  }

  async function baixarPdf() {
    if (!registro || baixando) return
    setBaixando(true)
    try {
      const dataIso = registro.dataCertificado || new Date().toISOString()
      await baixarCertificadoPdf(registro.nome ?? '', dataIso)
    } catch (e) {
      console.error('[certificado] erro ao gerar PDF', e)
      setErro('Não foi possível gerar o PDF. Tente novamente.')
    } finally {
      setBaixando(false)
    }
  }

  if (carregando) {
    return (
      <div className="min-h-screen bg-[#F8F4EF] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#2D6A4F]" size={32} />
      </div>
    )
  }

  if (erro || !registro) {
    return (
      <div className="min-h-screen bg-[#F8F4EF] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 border border-[#EDE0D4] shadow-sm text-center">
          <AlertCircle className="mx-auto text-[#B08968] mb-3" size={36} />
          <p className="text-[#2C2C2A] mb-6">{erro || 'Não foi possível carregar seu certificado.'}</p>
          <Link
            to="/curso"
            className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white font-medium px-6 py-3 rounded-full hover:bg-[#1f4f3a] transition-colors"
          >
            Voltar ao curso
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    )
  }

  const dataCertificado = registro.dataCertificado
    ? new Date(registro.dataCertificado)
    : new Date()
  const dataFormatada = dataCertificado.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })

  const nomeContato = (() => {
    if (!registro) return 'a Mônica'
    const indicadoPor = registro.indicadoPor ?? ''
    if (!indicadoPor || indicadoPor.toLowerCase() === 'monica') return 'a Mônica'
    // Busca feita no useEffect; usa o nome da consultora se disponível
    return nomeConsultora || 'a Mônica'
  })()

  const mensagemProximoPasso = encodeURIComponent(
    'Olá! Acabei de concluir o curso Uma Gota de Equilíbrio e quero dar o próximo passo. Pode me ajudar?'
  )
  const linkWhatsappKit = `https://wa.me/${wppContato}?text=${mensagemProximoPasso}`

  return (
    <div className="min-h-screen bg-[#F8F4EF] py-10 sm:py-14 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Certificado (preview na tela) */}
        <div className="bg-white border-[6px] sm:border-[8px] border-[#2D6A4F] rounded-3xl p-6 sm:p-12 shadow-xl relative overflow-hidden">
          {/* Decoração */}
          <div className="absolute top-3 left-3 right-3 bottom-3 border border-[#52B788]/40 rounded-2xl pointer-events-none" />

          <div className="relative text-center">
            <Award className="mx-auto text-[#B08968] mb-3" size={56} />
            <p className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#B08968] font-medium mb-2">
              Certificado de Conclusão
            </p>

            <p className="text-[#5F5E5A] text-sm sm:text-base mb-2">Certificamos que</p>
            <h1 className="font-cormorant text-3xl sm:text-5xl font-semibold text-[#2D6A4F] mb-3 leading-tight">
              {registro.nome || 'Participante'}
            </h1>
            <p className="text-[#5F5E5A] text-sm sm:text-base mb-5">concluiu com êxito o curso</p>

            <h2 className="font-cormorant text-2xl sm:text-3xl font-semibold text-[#2C2C2A] mb-1">
              Uma Gota de Equilíbrio
            </h2>
            <p className="text-sm text-[#5F5E5A] mb-1">Curso Introdutório de Óleos Essenciais</p>
            <p className="text-sm text-[#5F5E5A] mb-7">Aromaterapia Introdutória</p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12 pt-6 border-t border-[#EDE0D4]">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#B08968] font-medium mb-1">
                  Data de conclusão
                </p>
                <p className="text-sm sm:text-base text-[#2C2C2A] font-medium">{dataFormatada}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#B08968] font-medium mb-1">
                  Emitido por
                </p>
                <p className="font-cormorant text-base sm:text-lg font-semibold text-[#2D6A4F]">
                  Mônica Maria
                </p>
                <p className="text-xs text-[#5F5E5A]">Aromaterapeuta e Terapeuta Integrativa</p>
                <p className="text-xs text-[#B08968]">@reacendaluz</p>
              </div>
            </div>
          </div>
        </div>

        {/* Botão de download — agora gera PDF e baixa direto */}
        <div className="text-center mt-6">
          <button
            type="button"
            onClick={baixarPdf}
            disabled={baixando}
            className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white font-medium px-6 py-3 rounded-full hover:bg-[#1f4f3a] transition-colors shadow-md disabled:opacity-60 disabled:cursor-wait"
          >
            {baixando ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Gerando PDF…
              </>
            ) : (
              <>
                <Download size={18} />
                Baixar meu certificado em PDF
              </>
            )}
          </button>
        </div>

        {/* Separador + Bloco próximo passo */}
        <div className="mt-10">
          <hr className="border-0 border-t border-[#A8DFC4]" />
          <div className="mt-8 text-center max-w-2xl mx-auto">
            <p className="text-3xl mb-4">🌿</p>
            <p className="text-[#5F5E5A] text-sm sm:text-base leading-relaxed mb-8">
              Parabéns por concluir o curso Uma Gota de Equilíbrio!<br /><br />
              Agora entre em contato para dar o próximo passo, adquirir seus óleos essenciais e começar a ter resultados crescentes.
            </p>
            <a
              href={linkWhatsappKit}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#2D6A4F] text-white font-semibold px-7 py-4 rounded-full hover:bg-[#1f4f3a] transition-colors shadow-md"
            >
              💬 Falar com {nomeContato}
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Certificado
